import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Send, Clock, CheckCircle, Search, FileText, Calendar, ChevronDown, ChevronUp, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { incidentsAPI, bookingsAPI } from '../../services/api';

const INCIDENT_TYPES = {
  worker_no_show: 'Worker No-Show',
  worker_unprofessional: 'Unprofessional Behavior',
  worker_safety: 'Safety Issue',
  customer_harassment: 'Customer Harassment',
  customer_unsafe_environment: 'Unsafe Environment',
  customer_payment_dispute: 'Payment Dispute',
  service_property_damage: 'Property Damage',
  service_incomplete_work: 'Incomplete Work',
  service_quality_issue: 'Quality Issue',
  other: 'Other',
};

const STATUS_CONFIG = {
  received: { label: 'Received', color: 'bg-[#D4AF37]/10 text-[#D4AF37]' },
  investigating: { label: 'Investigating', color: 'bg-amber-100 text-amber-700' },
  actions_taken: { label: 'Actions Taken', color: 'bg-[#F4C2C2]/15 text-purple-700' },
  outcome_findings: { label: 'Outcome/Findings', color: 'bg-indigo-100 text-indigo-700' },
  solved: { label: 'Solved', color: 'bg-[var(--success)]/15 text-green-700' },
};

const IncidentReportSection = ({ userRole = 'customer' }) => {
  const [incidents, setIncidents] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expandedIncident, setExpandedIncident] = useState(null);
  
  const [form, setForm] = useState({
    booking_id: '',
    incident_type: '',
    category: userRole === 'worker' ? 'customer_related' : 'worker_related',
    title: '',
    description: '',
    severity: 'medium'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [incidentsRes, bookingsRes] = await Promise.all([
        incidentsAPI.getAll(),
        bookingsAPI.getAll()
      ]);
      setIncidents(incidentsRes.data.incidents || []);
      // Filter bookings that can have incidents reported
      const validBookings = (bookingsRes.data || []).filter(b => 
        ['confirmed', 'completed', 'in_progress'].includes(b.status)
      );
      setBookings(validBookings);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.booking_id || !form.incident_type || !form.title || !form.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await incidentsAPI.create(form);
      toast.success('Incident report submitted successfully');
      setShowForm(false);
      setForm({
        booking_id: '',
        incident_type: '',
        category: userRole === 'worker' ? 'customer_related' : 'worker_related',
        title: '',
        description: '',
        severity: 'medium'
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit incident report');
    } finally {
      setSubmitting(false);
    }
  };

  const getIncidentTypes = () => {
    if (userRole === 'worker') {
      return {
        customer_harassment: 'Customer Harassment',
        customer_unsafe_environment: 'Unsafe Environment',
        customer_payment_dispute: 'Payment Dispute',
        other: 'Other'
      };
    }
    return {
      worker_no_show: 'Worker No-Show',
      worker_unprofessional: 'Unprofessional Behavior',
      worker_safety: 'Safety Issue',
      service_property_damage: 'Property Damage',
      service_incomplete_work: 'Incomplete Work',
      service_quality_issue: 'Quality Issue',
      other: 'Other'
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin w-6 h-6 border-2 border-[var(--primary)] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Incident Reports</h2>
          <p className="text-sm text-[var(--text-muted)]">Report and track service incidents</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} className="btn-primary" data-testid="new-incident-btn">
          <AlertTriangle className="w-4 h-4 mr-2" />
          Report Incident
        </Button>
      </div>

      {/* New Incident Form */}
      {showForm && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">New Incident Report</h3>
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Select Booking *</Label>
              <Select value={form.booking_id} onValueChange={(v) => setForm({...form, booking_id: v})}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Choose the booking related to this incident" />
                </SelectTrigger>
                <SelectContent>
                  {bookings.map(b => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.service_name} - {b.scheduled_date} ({b.status})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Incident Type *</Label>
                <Select value={form.incident_type} onValueChange={(v) => setForm({...form, incident_type: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(getIncidentTypes()).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Severity</Label>
                <Select value={form.severity} onValueChange={(v) => setForm({...form, severity: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({...form, title: e.target.value})}
                placeholder="Brief summary of the incident"
                className="mt-1"
                data-testid="incident-title-input"
              />
            </div>

            <div>
              <Label>Description *</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({...form, description: e.target.value})}
                placeholder="Describe what happened in detail (minimum 20 characters)"
                className="mt-1 min-h-[100px]"
                data-testid="incident-description-input"
              />
            </div>

            <div className="flex gap-3 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} className="btn-primary" data-testid="submit-incident-btn">
                {submitting ? 'Submitting...' : 'Submit Report'}
              </Button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Incidents List */}
      {incidents.length === 0 ? (
        <div className="card p-12 text-center">
          <CheckCircle className="w-12 h-12 text-[var(--success)] mx-auto mb-4" />
          <h3 className="font-semibold mb-2">No Incidents Reported</h3>
          <p className="text-[var(--text-muted)]">You haven't reported any incidents yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {incidents.map(incident => {
            const isExpanded = expandedIncident === incident.id;
            const statusConfig = STATUS_CONFIG[incident.status] || STATUS_CONFIG.received;
            
            return (
              <div key={incident.id} className="card overflow-hidden" data-testid={`incident-card-${incident.id}`}>
                <div 
                  className="p-4 cursor-pointer hover:bg-[var(--background-subtle)]"
                  onClick={() => setExpandedIncident(isExpanded ? null : incident.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs px-2 py-1 rounded-full ${statusConfig.color}`}>
                          {statusConfig.label}
                        </span>
                        <span className="text-xs text-[var(--text-muted)]">
                          {new Date(incident.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <h3 className="font-semibold">{incident.title}</h3>
                      <p className="text-sm text-[var(--text-muted)]">
                        {INCIDENT_TYPES[incident.incident_type] || incident.incident_type}
                      </p>
                    </div>
                    {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </div>
                </div>
                
                {isExpanded && (
                  <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} className="border-t">
                    <div className="p-4 space-y-4">
                      <div className="p-3 bg-[var(--background-subtle)] rounded-lg">
                        <p className="text-sm font-medium mb-1">Description</p>
                        <p className="text-sm text-[var(--text-secondary)]">{incident.description}</p>
                      </div>
                      
                      {incident.booking_info && (
                        <div className="p-3 bg-[#D4AF37]/10 rounded-lg">
                          <p className="text-sm font-medium mb-2 flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            Related Booking
                          </p>
                          <p className="text-sm">{incident.booking_info.service_name} - {incident.booking_info.scheduled_date}</p>
                        </div>
                      )}
                      
                      {incident.status_history?.length > 0 && (
                        <div>
                          <p className="text-sm font-medium mb-2 flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            Status Updates
                          </p>
                          <div className="space-y-2 max-h-40 overflow-y-auto">
                            {incident.status_history.map((entry, idx) => (
                              <div key={`${entry.status}-${entry.updated_at}-${idx}`} className="text-sm p-2 bg-[var(--background-subtle)] rounded">
                                <p className="font-medium">{STATUS_CONFIG[entry.status]?.label}</p>
                                <p className="text-[var(--text-muted)]">{entry.notes}</p>
                                <p className="text-xs text-[var(--text-muted)] mt-1">
                                  {new Date(entry.updated_at).toLocaleString()}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default IncidentReportSection;
